import { StreamSession, StreamSessionClosedError } from "@lafayette/api-client";
import { type Channel, type LocalMessage, StreamChat } from "stream-chat";

import { type RawMessage, toThreadMessage } from "./message-mapping";
import {
  type ConnectionState,
  type ConnectMessaging,
  type MessagingConnection,
  type MessagingSessionInput,
  type OutgoingMessage,
  type SendFailure,
  type SendResult,
  type ThreadClient,
  ThreadClientError,
  type ThreadSnapshot,
} from "./thread-client";

// THE ONLY module of the admin that imports `stream-chat` (CHT-732, ADR 0009; docs/engineering/staff-workspace.md). Everything else in
// `src/conversations` reads `MessagingConnection`, `ThreadClient` and `ThreadMessage`. A test replaces this file with
// `test/fake-messaging.ts`.
//
// Why a thin UI over the `stream-chat` core and not `stream-chat-react`:
//  1. The accidental-send boundary. The composer must be disabled with a visible reason while a handoff is WAITING or the user is not
//     the owner. With the SDK's `MessageInput` that rule would live in props of someone else's component; here it is OUR composer, and
//     this file is the only code that can write to the customer channel.
//  2. Message ids. Contract section 3.5 needs a lowercase uuid v4 on every client message. The SDK default is `<userId>-<uuid>`. Here
//     the workspace sets the id, and nothing else can.
//  3. Lafayette cards and notices need our own message component anyway (the envelope `lf`), so the SDK list would be replaced.
//  4. No second design system and no SDK stylesheet in the admin bundle.
// The core `stream-chat` 9.54.0 is the same pin as `stream-chat-expo` uses on the phone (ADR 0009).
//
// The Stream API key is the PUBLIC key from the session route (never an environment variable of the admin build). The token comes from
// the same route; when the SDK needs a new one it calls `refreshToken`, and a refusal there ends the connection as `EXPIRED`.

const PAGE_SIZE = 30;

export type StreamConnectorDeps = Readonly<{
  /** Tests of this file pass a fake client. The app uses the real SDK. */
  createClient?: (apiKey: string) => StreamChat;
  now?: () => Date;
}>;

type Status = Readonly<{ status?: number }>;
const statusOf = (error: unknown): number | undefined => {
  const failure = error as { status?: unknown; response?: Status } | null;
  if (typeof failure?.status === "number") return failure.status;
  return failure?.response?.status;
};

function sendFailureOf(error: unknown, connection: ConnectionState): SendFailure {
  const status = statusOf(error);
  if (status === undefined) return connection === "ONLINE" ? "UNKNOWN" : "OFFLINE";
  if (status === 408 || status === 429 || status >= 500) return "UNKNOWN";
  return "REJECTED";
}

function rawOf(message: LocalMessage): RawMessage {
  const source = message as unknown as Record<string, unknown>;
  return {
    id: message.id,
    text: message.text,
    type: message.type,
    status: typeof source.status === "string" ? source.status : undefined,
    created_at: message.created_at,
    updated_at: message.updated_at,
    deleted_at: message.deleted_at,
    user: message.user ? { id: message.user.id, name: message.user.name } : null,
    attachments: message.attachments as RawMessage["attachments"],
    lf: source.lf,
  };
}

class StreamThreadClient implements ThreadClient {
  private channelRef: Channel | null = null;
  private snapshot: ThreadSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly cleanups: (() => void)[] = [];
  private readonly inflight = new Map<string, Promise<SendResult>>();
  /** What each undelivered message still needs: its text, the files not uploaded yet, the descriptors of those that were. Memory only. */
  private readonly outbox = new Map<
    string,
    { text: string; pending: File[]; uploaded: Record<string, unknown>[] }
  >();
  private loadingOlder = false;
  private started = false;
  private stopped = false;
  private rebuildQueued = false;

  constructor(
    private readonly connection: StreamMessagingConnection,
    private readonly makeChannel: () => Channel,
    private readonly ownUserId: string,
    private readonly now: () => Date,
  ) {
    this.snapshot = {
      messages: [],
      connection: connection.getState(),
      hasMoreOlder: false,
      loadingOlder: false,
      unreadCount: 0,
    };
  }

  // CHT-1984: the SDK throws on `client.channel()` until `connectUser` has run, and the connection starts only in `start()`. So the channel is
  // made on first use, after `ensureConnected()`. Made at once, a page that opened a thread before anything else had connected (the evidence
  // link of a memory fact, for a role that had not opened the inbox) crashed the whole console.
  private get channel(): Channel {
    this.channelRef ??= this.makeChannel();
    return this.channelRef;
  }

  getSnapshot = (): ThreadSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  async start(): Promise<void> {
    if (this.stopped) throw new ThreadClientError("UNAVAILABLE");
    try {
      await this.connection.ensureConnected();
      // The cleanup of the hook may have run while the connection was starting: then no channel is made and nothing is watched.
      if (this.stopped) return;
      await this.channel.watch({ messages: { limit: PAGE_SIZE } });
    } catch (error) {
      if (error instanceof ThreadClientError) throw error;
      const status = statusOf(error);
      if (status === 401) throw new ThreadClientError("SESSION", { cause: error });
      if (status === 403) throw new ThreadClientError("NOT_MEMBER", { cause: error });
      if (status === 404) throw new ThreadClientError("NOT_FOUND", { cause: error });
      throw new ThreadClientError("UNAVAILABLE", { cause: error });
    }
    if (this.stopped) {
      // The cleanup ran while `watch()` was in flight: `stop()` skipped `stopWatching()` because `started` was still false.
      try {
        await this.channel.stopWatching();
      } catch {
        // The connection may already be closed.
      }
      return;
    }
    this.cleanups.push(this.channel.on(() => this.queueRebuild()).unsubscribe);
    this.cleanups.push(this.connection.subscribe(() => this.queueRebuild()));
    this.started = true;
    this.rebuild();
  }

  async stop(): Promise<void> {
    this.stopped = true;
    for (const cleanup of this.cleanups.splice(0)) cleanup();
    this.listeners.clear();
    if (this.started) {
      try {
        await this.channel.stopWatching();
      } catch {
        // The connection may already be closed.
      }
    }
  }

  async loadOlder(): Promise<void> {
    if (!this.started || this.loadingOlder || !this.channel.state.messagePagination.hasPrev) return;
    const oldest = this.channel.state.messages[0];
    if (oldest === undefined) return;
    this.loadingOlder = true;
    this.rebuild();
    try {
      await this.channel.query({ messages: { id_lt: oldest.id, limit: PAGE_SIZE } });
    } catch (error) {
      // The list stays as it is and the button is still there. The caller learns that the page did not load (the cited-message search must
      // not read a failed request as the end of the history).
      throw new ThreadClientError("UNAVAILABLE", { cause: error });
    } finally {
      this.loadingOlder = false;
      this.rebuild();
    }
  }

  send(message: OutgoingMessage): Promise<SendResult> {
    const existing = this.inflight.get(message.id);
    if (existing !== undefined) return existing;
    if (!this.started) return Promise.resolve({ ok: false, reason: "UNKNOWN" });
    const user = this.channel.getClient().user;
    const now = new Date();
    // Shown at once as "sending", like the SDK components do, and replaced by the sent message or marked failed.
    this.channel.state.addMessageSorted(
      {
        id: message.id,
        text: message.text,
        type: "regular",
        status: "sending",
        cid: this.channel.cid,
        user,
        created_at: now,
        updated_at: now,
        deleted_at: null,
        pinned_at: null,
        // The files show by name while they upload and after a failure; the real addresses replace them once Stream answers.
        attachments: message.files.map((file) => ({
          type: file.type.startsWith("image/") ? "image" : "file",
          title: file.name,
          mime_type: file.type,
          file_size: file.size,
        })),
      } as unknown as LocalMessage,
      true,
    );
    this.outbox.set(message.id, { text: message.text, pending: [...message.files], uploaded: [] });
    this.queueRebuild();
    return this.run(message.id);
  }

  retry(messageId: string): Promise<SendResult> {
    const existing = this.inflight.get(messageId);
    if (existing !== undefined) return existing;
    const local = this.channel.state.findMessage(messageId);
    if (local === undefined) return Promise.resolve({ ok: false, reason: "REJECTED" });
    if ((local as unknown as { status?: string }).status === "received") {
      return Promise.resolve({ ok: true });
    }
    // The outbox holds the files that were not uploaded yet and the descriptors of those that were, so a retry sends the whole message
    // and never the text alone. An entry is missing only for a message this client did not send: it keeps what Stream has stored.
    if (!this.outbox.has(messageId)) {
      this.outbox.set(messageId, {
        text: local.text ?? "",
        pending: [],
        uploaded: (local.attachments ?? [])
          .filter(
            (attachment) =>
              attachment.image_url !== undefined || attachment.asset_url !== undefined,
          )
          .map((attachment) => ({ ...attachment }) as Record<string, unknown>),
      });
    }
    this.channel.state.addMessageSorted({ ...local, status: "sending" } as LocalMessage, true);
    this.queueRebuild();
    return this.run(messageId);
  }

  discard(messageId: string): void {
    if (this.inflight.has(messageId)) return;
    const local = this.channel.state.findMessage(messageId);
    if (local === undefined) return;
    // Only a message that never reached the thread can be dropped. A delivered one is the customer's to see and stays.
    if ((local as unknown as { status?: string }).status !== "failed") return;
    this.outbox.delete(messageId);
    this.channel.state.removeMessage({ id: messageId });
    this.queueRebuild();
  }

  subscribeMembership(listener: () => void): () => void {
    const unsubscribers = ["member.added", "member.removed", "member.updated"].map(
      (type) => this.channel.on(type as "member.added", () => listener()).unsubscribe,
    );
    this.cleanups.push(...unsubscribers);
    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }

  async markRead(): Promise<void> {
    if (!this.started || this.connection.getState() !== "ONLINE") return;
    try {
      await this.channel.markRead();
    } catch {
      // The next call repairs it.
    }
    this.queueRebuild();
  }

  // --- internals -----------------------------------------------------------------------------------------------

  private run(id: string): Promise<SendResult> {
    const attempt = this.deliver(id).finally(() => {
      this.inflight.delete(id);
    });
    this.inflight.set(id, attempt);
    return attempt;
  }

  private async deliver(id: string): Promise<SendResult> {
    const entry = this.outbox.get(id);
    if (entry === undefined) return { ok: false, reason: "REJECTED" };
    try {
      // A file moves from `pending` to `uploaded` as soon as Stream has stored it, so a retry after a failed send uploads only what is
      // missing and the message always goes out with ALL its files.
      for (const file of [...entry.pending]) {
        const isImage = file.type.startsWith("image/");
        const response = isImage
          ? await this.channel.sendImage(file, file.name, file.type)
          : await this.channel.sendFile(file, file.name, file.type);
        entry.uploaded.push(
          isImage
            ? {
                type: "image",
                image_url: response.file,
                title: file.name,
                mime_type: file.type,
                file_size: file.size,
              }
            : {
                type: "file",
                asset_url: response.file,
                title: file.name,
                mime_type: file.type,
                file_size: file.size,
              },
        );
        entry.pending.shift();
      }
      // The id is the workspace's uuid (contract 3.5). Only this call writes to the customer channel.
      const response = await this.channel.sendMessage({
        id,
        text: entry.text,
        attachments: entry.uploaded,
      } as never);
      this.outbox.delete(id);
      if (response?.message) {
        this.channel.state.addMessageSorted(
          { ...response.message, status: "received" } as unknown as LocalMessage,
          true,
        );
      }
      this.queueRebuild();
      return { ok: true };
    } catch (error) {
      // The send may have reached the server and lost its answer: if the message is there, it is sent.
      if (await this.confirmDelivered(id)) {
        this.outbox.delete(id);
        this.queueRebuild();
        return { ok: true };
      }
      const local = this.channel.state.findMessage(id);
      if (local !== undefined) {
        this.channel.state.addMessageSorted({ ...local, status: "failed" } as LocalMessage, true);
      }
      this.queueRebuild();
      return { ok: false, reason: sendFailureOf(error, this.connection.getState()) };
    }
  }

  private async confirmDelivered(id: string): Promise<boolean> {
    try {
      const found = await this.channel.getClient().getMessage(id);
      return found?.message?.id === id;
    } catch {
      return false;
    }
  }

  private queueRebuild(): void {
    if (this.rebuildQueued || this.stopped) return;
    this.rebuildQueued = true;
    queueMicrotask(() => {
      this.rebuildQueued = false;
      this.rebuild();
    });
  }

  private rebuild(): void {
    if (this.stopped) return;
    const now = this.now();
    this.snapshot = {
      messages: this.channel.state.messages.map((message) =>
        toThreadMessage(rawOf(message), { ownUserId: this.ownUserId, now }),
      ),
      connection: this.connection.getState(),
      hasMoreOlder: this.channel.state.messagePagination.hasPrev,
      loadingOlder: this.loadingOlder,
      unreadCount: this.channel.countUnread(),
    };
    for (const listener of [...this.listeners]) listener();
  }
}

class StreamMessagingConnection implements MessagingConnection {
  private readonly client: StreamChat;
  private readonly session: StreamSession<Channel>;
  private state: ConnectionState = "CONNECTING";
  private readonly listeners = new Set<() => void>();
  private connecting: Promise<void> | null = null;
  /** Set by the token provider when the session route refuses a refresh: the connection then reports `EXPIRED`. */
  private tokenRefused = false;

  constructor(
    private readonly sessionInput: MessagingSessionInput,
    refreshToken: () => Promise<string>,
    private readonly deps: StreamConnectorDeps,
  ) {
    // The connect order, the token provider, the stop guard and the disconnect are the shared lifecycle (`StreamSession`, CHT-1996). What
    // stays here is the state of the workspace: the words of the connection and the failures that the workspace shows.
    this.client =
      deps.createClient?.(sessionInput.apiKey) ??
      new StreamChat(sessionInput.apiKey, { timeout: 15_000 });
    this.session = new StreamSession<Channel>(
      this.client,
      { id: sessionInput.userId },
      {
        firstToken: sessionInput.token,
        refreshToken,
        onTokenRefused: () => {
          this.tokenRefused = true;
          this.setState("EXPIRED");
        },
      },
    );
  }

  getState = (): ConnectionState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private setState(next: ConnectionState): void {
    if (this.state === next || (this.state === "EXPIRED" && next !== "ONLINE")) return;
    this.state = next;
    for (const listener of [...this.listeners]) listener();
  }

  /** Connects the staff user once. The SDK joins no channel by itself. Throws `ThreadClientError`. */
  ensureConnected(): Promise<void> {
    if (this.session.closed) return Promise.reject(new ThreadClientError("UNAVAILABLE"));
    this.connecting ??= this.connect().catch((error: unknown) => {
      this.connecting = null;
      throw error;
    });
    return this.connecting;
  }

  private async connect(): Promise<void> {
    try {
      await this.session.connect();
    } catch (error) {
      if (error instanceof StreamSessionClosedError) throw new ThreadClientError("UNAVAILABLE");
      if (this.tokenRefused || statusOf(error) === 401) {
        this.setState("EXPIRED");
        throw new ThreadClientError("SESSION", { cause: error });
      }
      this.setState("OFFLINE");
      throw new ThreadClientError("UNAVAILABLE", { cause: error });
    }
    this.setState("ONLINE");
    this.session.listen("connection.changed", (event) => {
      this.setState(event.online === false ? "OFFLINE" : "ONLINE");
    });
  }

  openThread(channel: Readonly<{ type: "messaging"; id: string }>): ThreadClient {
    return new StreamThreadClient(
      this,
      () => this.session.channel(channel.type, channel.id),
      this.sessionInput.userId,
      this.deps.now ?? (() => new Date()),
    );
  }

  async unreadCounts(channelIds: readonly string[]): Promise<Readonly<Record<string, number>>> {
    const counts: Record<string, number> = {};
    for (const id of channelIds) counts[id] = 0;
    if (channelIds.length === 0) return counts;
    try {
      await this.ensureConnected();
      const channels = await this.client.queryChannels(
        {
          type: "messaging",
          id: { $in: [...channelIds] },
          members: { $in: [this.sessionInput.userId] },
        },
        {},
        { state: true, watch: false, limit: 100 },
      );
      for (const channel of channels) {
        if (channel.id !== undefined) counts[channel.id] = channel.countUnread();
      }
    } catch {
      // The overlay is optional. The inbox never depends on Stream.
    }
    return counts;
  }

  async disconnect(): Promise<void> {
    this.listeners.clear();
    await this.session.disconnect();
  }
}

/** The real connector. It connects at once (the runtime loads this module on first use, see `admin-runtime.ts`). */
export function createStreamConnector(
  deps: StreamConnectorDeps = {},
): (input: Parameters<ConnectMessaging>[0]) => MessagingConnection {
  return ({ session, refreshToken }) => new StreamMessagingConnection(session, refreshToken, deps);
}
